import { HttpParams } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, Subject, Subscription, firstValueFrom } from 'rxjs';

import {
  GET_NOTIFICATION_CATALOGUE_ENDPOINT,
  GET_NOTIFICATION_PREFERENCES_ENDPOINT,
  GET_NOTIFICATIONS_ENDPOINT,
  GET_NOTIFICATIONS_UNREAD_COUNT_ENDPOINT,
  GET_NOTIFICATIONS_UNSEEN_COUNT_ENDPOINT,
  POST_NOTIFICATION_OPENED_ENDPOINT,
  POST_NOTIFICATION_READ_ENDPOINT,
  POST_NOTIFICATIONS_READ_ENDPOINT,
  POST_NOTIFICATIONS_SEEN_ENDPOINT,
  POST_RESOURCE_NOTIFICATIONS_READ_ENDPOINT,
  PUT_NOTIFICATION_PREFERENCE_ENDPOINT,
} from '../api/api-endpoints';
import { RestApiService } from '../api/rest-api.service';
import {
  BrowserPushState,
  EMAIL_CLIENT_MESSAGE_RECEIVED_NOTIFICATION,
  FirebaseService,
} from './firebase.service';
import { TokenService } from './token.service';

export type { BrowserPushState } from './firebase.service';

export interface AppNotification {
  recipientId: string;
  id: string;
  notificationId: string;
  organisationId: string | null;
  resourceId: string | null;
  values: Record<string, string>;
  emailSubject: string;
  emailTemplate: string;
  smsTemplate: string;
  firebaseTemplate: string;
  readAt: string | null;
  seenAt: string | null;
  openedAt: string | null;
  emailSent: boolean;
  smsSent: boolean;
  firebaseSent: boolean;
  createdAt: string;
}

export interface NotificationPage {
  records: AppNotification[];
  page: number;
  limit: number;
  total_records: number;
  total_pages: number;
}

export interface NotificationChannelDefinition {
  enabled: boolean;
  subject: string | null;
  template: string;
}

export interface NotificationDefinition {
  id: string;
  label: string;
  group: string;
  route_template: string | null;
  employment_required: boolean;
  required_values: string[];
  email: NotificationChannelDefinition;
  sms: NotificationChannelDefinition;
  firebase: NotificationChannelDefinition;
}

export interface NotificationPreference {
  key: string;
  enabled: boolean;
}

export const EMPLOYMENT_INVITATION_NOTIFICATION = 'org:hr.employment.invitation-sent';

const HEADER_NOTIFICATION_LIMIT = 10;

@Injectable({ providedIn: 'root' })
export class NotificationsService implements OnDestroy {
  private readonly notificationsSubject = new BehaviorSubject<AppNotification[]>([]);
  private readonly unreadCountSubject = new BehaviorSubject<number>(0);
  private readonly unseenCountSubject = new BehaviorSubject<number>(0);
  private readonly refreshedSubject = new Subject<void>();
  private catalogueSnapshot: NotificationDefinition[] = [];
  private activeAccessToken?: string;
  private readonly firebaseEventsSubscription: Subscription;
  private refreshing = false;
  private refreshQueued = false;

  readonly notifications$ = this.notificationsSubject.asObservable();
  readonly unreadCount$ = this.unreadCountSubject.asObservable();
  readonly unseenCount$ = this.unseenCountSubject.asObservable();
  readonly browserPushState$: Observable<BrowserPushState>;
  readonly refreshed$ = this.refreshedSubject.asObservable();

  constructor(
    private readonly restApi: RestApiService,
    private readonly tokenService: TokenService,
    private readonly router: Router,
    private readonly firebase: FirebaseService,
  ) {
    this.browserPushState$ = this.firebase.state$;
    this.firebaseEventsSubscription = this.firebase.events$.subscribe((event) => {
      if (event.notification_id === EMAIL_CLIENT_MESSAGE_RECEIVED_NOTIFICATION) return;
      if (event.kind === 'notification' || event.event === 'notifications.updated') {
        void this.refresh().catch(() => undefined);
      }
    });
  }

  ngOnDestroy(): void {
    this.firebaseEventsSubscription.unsubscribe();
  }

  start(): void {
    const accessToken = this.tokenService.getAccessToken();
    if (!accessToken || this.activeAccessToken === accessToken) return;
    this.activeAccessToken = accessToken;
    void this.initialize(accessToken);
  }

  async stop(deactivateDevice = false): Promise<void> {
    await this.firebase.stop(deactivateDevice);
    this.activeAccessToken = undefined;
    this.notificationsSubject.next([]);
    this.unreadCountSubject.next(0);
    this.unseenCountSubject.next(0);
  }

  getSnapshot(): AppNotification[] {
    return [...this.notificationsSubject.value];
  }

  getUnreadCount(): number {
    return this.unreadCountSubject.value;
  }

  page(page: number, perPage: number): Observable<NotificationPage> {
    const params = new HttpParams()
      .set('page', Math.max(1, page))
      .set('perPage', Math.max(1, Math.min(100, perPage)));
    return this.restApi.get<NotificationPage>(GET_NOTIFICATIONS_ENDPOINT, { params });
  }

  catalogue(): Observable<NotificationDefinition[]> {
    return this.restApi.get<NotificationDefinition[]>(GET_NOTIFICATION_CATALOGUE_ENDPOINT);
  }

  preferences(): Observable<NotificationPreference[]> {
    return this.restApi.get<NotificationPreference[]>(GET_NOTIFICATION_PREFERENCES_ENDPOINT);
  }

  savePreference(key: string, enabled: boolean): Observable<NotificationPreference> {
    return this.restApi.put<NotificationPreference>(PUT_NOTIFICATION_PREFERENCE_ENDPOINT, {
      key,
      enabled,
    });
  }

  async refresh(): Promise<void> {
    if (!this.tokenService.getAccessToken()) return;
    if (this.refreshing) {
      this.refreshQueued = true;
      return;
    }
    this.refreshing = true;
    try {
      do {
        this.refreshQueued = false;
        const [page, unread, unseen] = await Promise.all([
          firstValueFrom(this.page(1, HEADER_NOTIFICATION_LIMIT)),
          firstValueFrom(
            this.restApi.get<{ unreadCount: number }>(GET_NOTIFICATIONS_UNREAD_COUNT_ENDPOINT),
          ),
          firstValueFrom(
            this.restApi.get<{ unseenCount: number }>(GET_NOTIFICATIONS_UNSEEN_COUNT_ENDPOINT),
          ),
        ]);
        this.notificationsSubject.next(page.records || []);
        this.unreadCountSubject.next(Math.max(0, Number(unread.unreadCount || 0)));
        this.unseenCountSubject.next(Math.max(0, Number(unseen.unseenCount || 0)));
        this.refreshedSubject.next();
      } while (this.refreshQueued && this.tokenService.getAccessToken());
    } finally {
      this.refreshing = false;
    }
  }

  async markAsSeen(notifications: AppNotification[]): Promise<void> {
    const recipientIds = notifications
      .filter((notification) => !notification.seenAt && !notification.recipientId.startsWith('local-'))
      .map((notification) => notification.recipientId);
    const localIds = notifications
      .filter((notification) => !notification.seenAt && notification.recipientId.startsWith('local-'))
      .map((notification) => notification.recipientId);
    if (recipientIds.length > 0) {
      await firstValueFrom(
        this.restApi.post<{ updatedCount: number }>(POST_NOTIFICATIONS_SEEN_ENDPOINT, {
          recipientIds,
        }),
      );
    }
    const seenIds = new Set([...recipientIds, ...localIds]);
    if (seenIds.size === 0) return;
    const seenAt = new Date().toISOString();
    this.notificationsSubject.next(
      this.notificationsSubject.value.map((notification) =>
        seenIds.has(notification.recipientId)
          ? { ...notification, seenAt: notification.seenAt || seenAt }
          : notification,
      ),
    );
    this.unseenCountSubject.next(
      Math.max(0, this.unseenCountSubject.value - seenIds.size),
    );
  }

  async markAsRead(recipientId: string): Promise<void> {
    if (!recipientId || recipientId.startsWith('local-')) {
      this.markLocalRead(recipientId);
      return;
    }
    const notification = this.notificationsSubject.value.find(
      (item) => item.recipientId === recipientId,
    );
    await firstValueFrom(this.restApi.post(POST_NOTIFICATION_READ_ENDPOINT(recipientId), {}));
    this.markLocalRead(recipientId);
    this.unreadCountSubject.next(Math.max(0, this.unreadCountSubject.value - 1));
    if (notification && !notification.seenAt) {
      this.unseenCountSubject.next(Math.max(0, this.unseenCountSubject.value - 1));
    }
  }

  async markAllAsRead(): Promise<void> {
    await firstValueFrom(this.restApi.post<{ updatedCount: number }>(POST_NOTIFICATIONS_READ_ENDPOINT, {}));
    this.notificationsSubject.next(
      this.notificationsSubject.value.map((notification) => ({
        ...notification,
        seenAt: notification.seenAt || new Date().toISOString(),
        readAt: notification.readAt || new Date().toISOString(),
      })),
    );
    this.unreadCountSubject.next(0);
    this.unseenCountSubject.next(0);
  }

  async markResourceAsRead(resourceId: string): Promise<void> {
    if (!resourceId) return;
    await firstValueFrom(
      this.restApi.post(POST_RESOURCE_NOTIFICATIONS_READ_ENDPOINT(resourceId), {}),
    );
    await this.refresh();
  }

  async open(notification: AppNotification): Promise<void> {
    if (notification.recipientId.startsWith('local-')) {
      this.markLocalOpened(notification.recipientId);
    } else if (!notification.openedAt) {
      await firstValueFrom(
        this.restApi.post(POST_NOTIFICATION_OPENED_ENDPOINT(notification.recipientId), {}),
      );
      this.markLocalOpened(notification.recipientId);
    }
    const route = this.routeFor(notification);
    if (route) await this.router.navigateByUrl(route);
  }

  titleFor(notification: AppNotification): string {
    const definition = this.definitionFor(notification.notificationId);
    return this.render(
      definition?.firebase.subject || notification.emailSubject || definition?.label || 'Notification',
      notification.values,
    );
  }

  messageFor(notification: AppNotification): string {
    const message = this.render(notification.firebaseTemplate, notification.values);
    if (message) return message;
    const emailText = this.render(notification.emailTemplate, notification.values)
      .replace(/<[^>]+>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    return emailText || 'A new notification was received.';
  }

  labelFor(notification: AppNotification): string {
    return this.definitionFor(notification.notificationId)?.label || notification.notificationId;
  }

  routeFor(notification: AppNotification): string | null {
    const template = this.definitionFor(notification.notificationId)?.route_template;
    if (!template) return null;
    const route = this.render(template, {
      ...notification.values,
      resource_id: notification.resourceId || '',
      notification_id: notification.id,
    });
    if (notification.notificationId === EMPLOYMENT_INVITATION_NOTIFICATION) {
      // Email links use the configured public hostname. Review them locally
      // through this single owner path; never navigate to the supplied host.
      try {
        const url = new URL(route, window.location.origin);
        const tokens = url.searchParams.getAll('token');
        const decisions = url.searchParams.getAll('decision');
        if (!['http:', 'https:'].includes(url.protocol) || url.username || url.password || url.hash
          || url.pathname !== '/employment/respond' || tokens.length !== 1 || !tokens[0].trim()
          || decisions.length > 1 || decisions.some(value => !['accept', 'deny'].includes(value))
          || Array.from(url.searchParams.keys()).some(key => key !== 'token' && key !== 'decision')) return null;
        return `${url.pathname}${url.search}`;
      } catch {
        return null;
      }
    }
    if (route.startsWith('/') && !route.startsWith('//')) return route;
    try {
      const url = new URL(route);
      if (url.origin !== window.location.origin) return null;
      return `${url.pathname}${url.search}${url.hash}`;
    } catch {
      return null;
    }
  }

  addLocalNotification(input: {
    title: string;
    message: string;
    event?: string;
    severity?: string;
    metadata?: Record<string, string>;
  }): void {
    const id = `local-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    this.notificationsSubject.next([
      {
        recipientId: id,
        id,
        notificationId: input.event || 'user:local.reminder.created',
        organisationId: null,
        resourceId: input.metadata?.['resource_id'] || null,
        values: input.metadata || {},
        emailSubject: input.title,
        emailTemplate: '',
        smsTemplate: '',
        firebaseTemplate: input.message,
        readAt: null,
        seenAt: null,
        openedAt: null,
        emailSent: false,
        smsSent: false,
        firebaseSent: false,
        createdAt: new Date().toISOString(),
      },
      ...this.notificationsSubject.value,
    ].slice(0, HEADER_NOTIFICATION_LIMIT));
    this.unreadCountSubject.next(this.unreadCountSubject.value + 1);
    this.unseenCountSubject.next(this.unseenCountSubject.value + 1);
  }

  async enableBrowserPush(): Promise<BrowserPushState> {
    return this.firebase.enable();
  }

  private async initialize(accessToken: string): Promise<void> {
    try {
      this.catalogueSnapshot = await firstValueFrom(this.catalogue());
    } catch {
      this.catalogueSnapshot = [];
    }
    try {
      await this.refresh();
    } catch {
      // Firebase still starts so a later push can recover notification data.
    }
    if (this.activeAccessToken === accessToken) this.firebase.start(true);
  }

  private definitionFor(notificationId: string): NotificationDefinition | undefined {
    return this.catalogueSnapshot.find((definition) => definition.id === notificationId);
  }

  private markLocalRead(recipientId: string): void {
    this.notificationsSubject.next(
      this.notificationsSubject.value.map((notification) =>
        notification.recipientId === recipientId
          ? {
              ...notification,
              seenAt: notification.seenAt || new Date().toISOString(),
              readAt: notification.readAt || new Date().toISOString(),
            }
          : notification,
      ),
    );
  }

  private markLocalOpened(recipientId: string): void {
    const notification = this.notificationsSubject.value.find(
      (item) => item.recipientId === recipientId,
    );
    if (!notification) return;
    const wasUnread = !notification.readAt;
    const wasUnseen = !notification.seenAt;
    const openedAt = new Date().toISOString();
    this.notificationsSubject.next(
      this.notificationsSubject.value.map((item) =>
        item.recipientId === recipientId
          ? {
              ...item,
              seenAt: item.seenAt || openedAt,
              openedAt: item.openedAt || openedAt,
              readAt: item.readAt || openedAt,
            }
          : item,
      ),
    );
    if (wasUnread) this.unreadCountSubject.next(Math.max(0, this.unreadCountSubject.value - 1));
    if (wasUnseen) this.unseenCountSubject.next(Math.max(0, this.unseenCountSubject.value - 1));
  }

  private render(template: string, values: Record<string, string>): string {
    return Object.entries(values || {}).reduce(
      (rendered, [key, value]) => rendered.replaceAll(`{{${key}}}`, String(value)),
      template || '',
    );
  }
}
